/**
 * Loads locale messages from JSON files in the './locales' directory.
 * The function uses webpack's `require.context` to dynamically import all JSON files
 * matching the specified pattern and constructs a messages object where each key is
 * the locale identifier and the value is the content of the corresponding JSON file.
 *
 * @returns {Object} An object containing locale messages.
 */

import Vue from 'vue';
import VueI18n from 'vue-i18n';
import LocalStorage from '@/services/LocalStorage';

/**
 * Vue.use is required to register VueI18n as a plugin in Vue 2.
 * This ensures compatibility with Vue 2 applications.
 */
Vue.use(VueI18n);

/**
 * Function to dynamically load locale messages from JSON files.
 * It scans the './locales' directory for JSON files and creates
 * an object where each key is a locale identifier.
 *
 * @returns {Object} An object containing locale messages.
 */
function loadLocaleMessages() {
  const locales = require.context(
    './locales',
    true,
    /[A-Za-z0-9-_,\s]+\.json$/i
  );
  const messages = {};
  locales.keys().forEach((key) => {
    const locale = key.match(/([A-Za-z0-9-_]+)\./i)[1];
    const message = locales(key);
    messages[locale] =
      typeof message === 'object' && message !== null ? message : {};
  });
  return messages;
}

/**
 * Creates an instance of VueI18n for internationalization support in the Vue application.
 * The `legacy` mode is set to true to ensure compatibility with Vue 2.
 * The locale and fallbackLocale are set based on environment variables or default values.
 * The messages are loaded dynamically using the `loadLocaleMessages` function.
 *
 * @type {Object}
 * @property {boolean} legacy - Ensures compatibility with Vue 2.
 * @property {string} locale - The default locale for the application.
 * @property {string} fallbackLocale - The fallback locale for the application.
 * @property {Object} messages - The loaded locale messages.
 * @property {boolean} silentTranslationWarn - Suppresses translation warnings in console.
 * @property {boolean} silentFallbackWarn - Suppresses fallback warnings in console.
 */

const i18n = new VueI18n({
  legacy: true, // Ensures compatibility with Vue 2
  locale:
    LocalStorage.get('language') || process.env.VUE_APP_I18N_LOCALE || 'pt-BR',
  fallbackLocale: process.env.VUE_APP_I18N_FALLBACK_LOCALE || 'pt-BR',
  messages: loadLocaleMessages(),
  silentTranslationWarn: true, // Suppresses translation warnings
  silentFallbackWarn: true, // Suppresses fallback warnings
  missing: (locale, key) => {
    console.warn(
      `[i18n] Missing translation for key "${key}" in locale "${locale}"`
    );
    return key; // Return the key as a fallback
  },
});

// Set default locale if not provided
if (!i18n.locale || typeof i18n.locale !== 'string') {
  // i18n.locale = 'pt-BR';
}

/**
 * Exports the VueI18n instance to be used globally in the application.
 */
export default i18n;
